import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import {
  AlertCircle, RefreshCw, ScanLine, CheckCircle2, XCircle, TriangleAlert,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const colorBadgeCls = {
  green: 'bg-green-100 text-green-800 border-green-300',
  orange: 'bg-orange-100 text-orange-800 border-orange-300',
  red: 'bg-red-100 text-red-800 border-red-300',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const ConfidencePills = ({ counts = {} }) => (
  <div className="flex gap-1.5 text-xs">
    <span className={`px-1.5 py-0.5 rounded border ${colorBadgeCls.green}`}>{counts.green || 0} OK</span>
    <span className={`px-1.5 py-0.5 rounded border ${colorBadgeCls.orange}`}>{counts.orange || 0} ⚠</span>
    <span className={`px-1.5 py-0.5 rounded border ${colorBadgeCls.red}`}>{counts.red || 0} ✕</span>
  </div>
);

const OcrLogsPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [stats, setStats] = useState(null);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

  const load = async (newOffset = 0, failed = onlyFailed) => {
    try {
      setLoading(true); setError('');
      const [{ data: list }, { data: stat }] = await Promise.all([
        axios.get(`${API}/admin/ocr-logs`, { ...authHeader(), params: { limit: PAGE_SIZE, offset: newOffset, only_failed: failed } }),
        axios.get(`${API}/admin/ocr-logs/stats`, authHeader()),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setOffset(newOffset);
      setStats(stat);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(0, onlyFailed); /* eslint-disable-next-line */ }, [onlyFailed]);

  const handlePurge = async () => {
    if (!window.confirm("Confirmer la suppression de TOUS les logs OCR ? Cette action est irréversible.")) return;
    try {
      await axios.delete(`${API}/admin/ocr-logs?confirm=yes`, authHeader());
      load(0, onlyFailed);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la purge.');
    }
  };

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="admin-ocr-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScanLine className="w-6 h-6 text-[#52a675]" /> Logs OCR — Carte grise
          </h1>
          <p className="text-sm text-gray-500">Journal anonymisé des scans (aucune image, ni nom, ni VIN, ni plaque).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => load(0, onlyFailed)} disabled={loading} data-testid="ocr-logs-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
          </Button>
          <Button variant="outline" className="text-red-600" onClick={handlePurge} data-testid="ocr-logs-purge">
            Purger
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Total scans</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Succès</p>
            <p className="text-2xl font-bold text-green-600">{stats.success}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Échecs</p>
            <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Champs lus (cumul)</p>
            <ConfidencePills counts={stats.color_totals || {}} />
          </CardContent></Card>
        </div>
      )}

      {/* Top warnings */}
      {stats && stats.top_warnings?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TriangleAlert className="w-4 h-4 text-orange-500" /> Top 10 avertissements
            </CardTitle>
            <CardDescription>Patterns les plus fréquents pour affiner le prompt.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {stats.top_warnings.map((w, i) => (
              <div key={i} className="flex justify-between gap-3 text-sm border-b last:border-b-0 py-1">
                <span className="text-gray-700">{w.warning}</span>
                <Badge variant="secondary">{w.count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyFailed}
            onChange={(e) => setOnlyFailed(e.target.checked)}
            data-testid="ocr-logs-failed-filter"
          />
          Afficher uniquement les échecs
        </label>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ocr-logs-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left p-3 font-medium">Date</th>
                <th className="text-left p-3 font-medium">Statut</th>
                <th className="text-left p-3 font-medium">Marque / Modèle</th>
                <th className="text-left p-3 font-medium">Cylindrée / kW</th>
                <th className="text-left p-3 font-medium">Champs</th>
                <th className="text-left p-3 font-medium">Avertissements</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-gray-500">Chargement…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-gray-500">Aucun log pour le moment.</td></tr>
              ) : items.map((row) => {
                const d = row.data || {};
                const open = !!expanded[row.id];
                return (
                  <React.Fragment key={row.id}>
                    <tr className="border-t hover:bg-gray-50">
                      <td className="p-3 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      <td className="p-3">
                        {row.success
                          ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="w-4 h-4" /> OK</span>
                          : <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="w-4 h-4" /> Échec</span>}
                      </td>
                      <td className="p-3">
                        {d.marque || '—'} {d.modele ? <span className="text-gray-500"> / {d.modele}</span> : null}
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        {d.cylindree_cm3 ? `${d.cylindree_cm3} cm³` : '—'}
                        <span className="text-gray-400 mx-1">·</span>
                        {d.puissance_kw ? `${d.puissance_kw} kW` : '—'}
                      </td>
                      <td className="p-3"><ConfidencePills counts={row.color_counts || {}} /></td>
                      <td className="p-3">
                        {row.warnings?.length > 0 ? (
                          <button
                            onClick={() => toggle(row.id)}
                            className="text-orange-700 underline text-xs"
                            data-testid={`ocr-log-expand-${row.id}`}
                          >
                            {row.warnings.length} {row.warnings.length > 1 ? 'avertissements' : 'avertissement'} {open ? '▲' : '▼'}
                          </button>
                        ) : (
                          row.error ? <span className="text-red-700 text-xs">{row.error}</span> : <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                    {open && row.warnings?.length > 0 && (
                      <tr><td colSpan="6" className="bg-orange-50/40 p-3">
                        <ul className="list-disc list-inside text-sm text-orange-900 space-y-1">
                          {row.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          {total === 0 ? 'Aucun résultat' : `Affichage ${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} sur ${total}`}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0 || loading} onClick={() => load(Math.max(0, offset - PAGE_SIZE), onlyFailed)}>Précédent</Button>
          <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total || loading} onClick={() => load(offset + PAGE_SIZE, onlyFailed)}>Suivant</Button>
        </div>
      </div>
    </div>
  );
};

export default OcrLogsPage;
